// Display-layer formatting only. The engine never rounds internally (see engine.ts);
// this module is where locale + currency formatting actually happens.

import type { CalculatorCurrency } from "./types";
import type { Locale } from "@/i18n";

const LOCALE_TAG: Record<Locale, string> = {
  en: "en-GB",
  de: "de-DE",
  fr: "fr-FR",
  es: "es-ES",
  it: "it-IT",
};

export function formatCurrency(
  value: number,
  currency: CalculatorCurrency,
  locale: Locale,
  options: { maximumFractionDigits?: number } = {},
): string {
  const safe = Number.isFinite(value) ? value : 0;
  try {
    return new Intl.NumberFormat(LOCALE_TAG[locale] ?? "en-GB", {
      style: "currency",
      currency,
      maximumFractionDigits: options.maximumFractionDigits ?? 0,
    }).format(safe);
  } catch {
    return `${safe.toFixed(0)} ${currency}`;
  }
}

/**
 * Compact currency for chart axis ticks ("€180K", "$1.2M") so long values never
 * get clipped by a fixed axis width.
 */
export function formatCompactCurrency(
  value: number,
  currency: CalculatorCurrency,
  locale: Locale,
): string {
  const safe = Number.isFinite(value) ? value : 0;
  try {
    return new Intl.NumberFormat(LOCALE_TAG[locale] ?? "en-GB", {
      style: "currency",
      currency,
      notation: "compact",
      compactDisplay: "short",
      maximumFractionDigits: Math.abs(safe) >= 1_000_000 ? 1 : 0,
    }).format(safe);
  } catch {
    return formatCurrency(safe, currency, locale, { maximumFractionDigits: 0 });
  }
}

export function formatNumber(value: number, locale: Locale, maximumFractionDigits = 1): string {
  const safe = Number.isFinite(value) ? value : 0;
  try {
    return new Intl.NumberFormat(LOCALE_TAG[locale] ?? "en-GB", { maximumFractionDigits }).format(
      safe,
    );
  } catch {
    return safe.toFixed(maximumFractionDigits);
  }
}

export function formatPercent(value: number, locale: Locale, maximumFractionDigits = 1): string {
  return `${formatNumber(value, locale, maximumFractionDigits)}%`;
}

/**
 * Parses a locale-formatted number string the user typed (supports both "," and "."
 * as the decimal separator depending on locale) into a plain JS number, or null if it
 * cannot be parsed. Used on blur / dedicated display layer – never while typing, per
 * spec (aggressive live reformatting breaks input editing).
 */
export function parseLocaleNumber(input: string, locale: Locale): number | null {
  const trimmed = input.trim();
  if (trimmed === "") return null;
  const usesCommaDecimal = locale === "de" || locale === "es" || locale === "it";
  let normalized = trimmed;
  if (usesCommaDecimal) {
    // "1.234,56" -> "1234.56"
    normalized = normalized.replace(/\./g, "").replace(",", ".");
  } else if (locale === "fr") {
    // "1 234,56" -> "1234.56"
    normalized = normalized.replace(/\s/g, "").replace(",", ".");
  } else {
    // en: "1,234.56" -> "1234.56"
    normalized = normalized.replace(/,/g, "");
  }
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

/**
 * Formats an ISO date (YYYY-MM-DD) as a localized month + year, used for the
 * "assumption last reviewed" line. Fixed to UTC so SSR and client agree.
 */
export function formatReviewDate(iso: string, locale: Locale): string {
  const date = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "long",
    timeZone: "UTC",
  }).format(date);
}
